<template>
  <div class="bv-report">
    <div class="bv-report__band">
      <div class="bv-report__left">
        <div class="bv-report__score-eyebrow">Verification score</div>
        <div class="bv-report__score" :class="`bv-report__score--${tierColor}`">
          {{ verification.score }}<span class="bv-report__score-den">/100</span>
        </div>
        <div class="bv-report__tier" :class="`bv-report__tier--${tierColor}`">{{ tierLabel }}</div>
        <div class="bv-report__ledger">
          <div v-for="d in verification.deductions" :key="d.reason" class="bv-report__ledger-row">
            <span class="bv-report__ledger-reason">{{ d.reason }}</span>
            <span class="bv-report__ledger-neg">−{{ d.points }}</span>
          </div>
          <div v-if="!verification.deductions?.length" class="bv-report__ledger-clean">Clean call — no deductions</div>
        </div>
        <div v-if="signalCaption" class="bv-report__signals">{{ signalCaption }}</div>
      </div>

      <div class="bv-report__right">
        <div class="bv-report__captured-num">
          {{ verification.capturedCount }}<span class="bv-report__captured-den">/{{ verification.expectedCount }}</span>
        </div>
        <div class="bv-report__captured-label">fields captured</div>
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          class="bv-report__reverify"
          prepend-icon="mdi-phone-refresh"
          @click="emit('re-verify')">
          Call again
        </v-btn>
        <div class="bv-report__actions">
          <v-btn size="x-small" variant="text" prepend-icon="mdi-download" @click="exportCsv">CSV</v-btn>
          <v-btn size="x-small" variant="text" prepend-icon="mdi-printer" @click="exportPdf">PDF</v-btn>
        </div>
      </div>
    </div>

    <div class="bv-report__table-wrap">
      <table class="bv-report__table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Captured</th>
            <th class="bv-report__th-right">Heard at</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groupedRows" :key="group.label">
            <tr class="bv-report__group-row">
              <td colspan="3">{{ group.label }}</td>
            </tr>
            <tr v-for="row in group.rows" :key="row.key" :class="{ 'bv-report__row--empty': row.empty }">
              <td class="bv-report__cell-field">{{ row.label }}</td>
              <td class="bv-report__cell-value">
                <span v-if="!row.empty">{{ row.value }}</span>
                <span v-else class="bv-report__unanswered">unanswered</span>
              </td>
              <td class="bv-report__cell-ts">
                <button
                  v-if="row.segmentIndex !== null"
                  type="button"
                  class="bv-report__ts-chip"
                  @click="emit('jump', row.segmentIndex)">
                  {{ row.timestamp }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  call: { type: Object, required: true },
  transcriptSegments: { type: Array, default: () => [] },
});
const emit = defineEmits(["jump", "re-verify"]);

const META_KEYS = new Set([
  "outcome",
  "notes",
  "repName",
  "repTitle",
  "captureConfidence",
  "repReliabilityFlags",
  "callReferenceNumber",
]);

const GROUPS = [
  {
    label: "Plan basics",
    keys: [
      "eligibilityStatus",
      "isPrimaryInsurance",
      "subscriberName",
      "planName",
      "planYearType",
      "fundingType",
      "situsState",
      "inNetworkStatus",
      "coverageType",
    ],
  },
  {
    label: "Accumulators & maximums",
    keys: [
      "deductibleIndividual",
      "outOfPocketIndividual",
      "coinsurance",
      "copay",
      "lifetimeMax",
      "annualMax",
      "cycleLimits",
    ],
  },
  {
    label: "Policy & requirements",
    keys: [
      "medicalPolicy",
      "infertilityCriteria",
      "day3Labs",
      "hsgStepProtocol",
      "priorAuthRequired",
      "priorAuthEntity",
    ],
  },
  {
    label: "Services & carve-outs",
    keys: [
      "ivfCoverage",
      "iuiCoverage",
      "cryoCoverage",
      "storageCoverage",
      "pbmName",
      "thirdPartyVendor",
      "exclusions",
    ],
  },
];

const FIELD_LABELS = {
  eligibilityStatus: "Eligibility",
  isPrimaryInsurance: "Primary insurance",
  subscriberName: "Subscriber",
  planName: "Plan name",
  planYearType: "Plan year",
  fundingType: "Funding type",
  situsState: "Policy state",
  inNetworkStatus: "Network status",
  coverageType: "Coverage type",
  deductibleIndividual: "Individual deductible",
  outOfPocketIndividual: "Individual OOP max",
  coinsurance: "Coinsurance",
  copay: "Copay",
  lifetimeMax: "Lifetime maximum",
  annualMax: "Annual maximum",
  cycleLimits: "Cycle limits",
  medicalPolicy: "Medical policy",
  infertilityCriteria: "Infertility criteria",
  day3Labs: "Day-3 labs",
  hsgStepProtocol: "HSG / step protocol",
  priorAuthRequired: "Prior authorization",
  priorAuthEntity: "PA entity",
  ivfCoverage: "IVF",
  iuiCoverage: "IUI",
  cryoCoverage: "Cryopreservation",
  storageCoverage: "Storage",
  pbmName: "PBM",
  thirdPartyVendor: "Third-party vendor",
  exclusions: "Exclusions",
};

const verification = computed(() => props.call?.metadata?.verification || {});
const signals = computed(() => props.call?.metadata?.qualitySignals || {});

const TIERS = {
  verified: { label: "Verified", color: "green", chip: "green" },
  re_verify: { label: "Follow-up suggested", color: "amber", chip: "amber-darken-2" },
  re_verify_escalate: { label: "Follow-up needed", color: "red", chip: "red" },
};
const tierLabel = computed(() => TIERS[verification.value.tier]?.label || verification.value.tier || "—");
const tierColor = computed(() => TIERS[verification.value.tier]?.color || "amber");

function fmtSeconds(total) {
  if (total == null) return "";
  const m = Math.floor(total / 60);
  const s = Math.round(total % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
}

const signalCaption = computed(() => {
  const s = signals.value;
  const parts = [];
  if (s.repGapMedianS != null) parts.push(`typical answer ${s.repGapMedianS}s`);
  if (s.longPauseCount) parts.push(`${s.longPauseCount} long pause${s.longPauseCount === 1 ? "" : "s"}`);
  if (s.extendedHoldCount) parts.push(`${fmtSeconds(s.holdTimeS)} on hold`);
  return parts.join("  ·  ");
});

function snakeToCamel(s) {
  return String(s).replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

function fieldLabel(key) {
  const camel = snakeToCamel(key);
  return (
    FIELD_LABELS[camel] ||
    camel
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase())
  );
}

// Find the transcript turn where a captured value was (most likely) stated: first rep turn whose
// text contains a distinctive fragment of the value. Returns null when no confident match.
function locateSegment(value) {
  if (!value || !props.transcriptSegments.length) return null;
  const fragments = String(value)
    .split(/[·,;()]/)
    .map((f) => f.trim().toLowerCase())
    .filter((f) => f.length >= 3);
  for (let i = 0; i < props.transcriptSegments.length; i++) {
    const seg = props.transcriptSegments[i];
    if (seg.role !== "user") continue;
    const text = (seg.message || seg.text || "").toLowerCase();
    if (!text) continue;
    if (fragments.some((f) => text.includes(f))) return i;
  }
  return null;
}

function segmentTimestamp(index) {
  const seg = props.transcriptSegments[index];
  const t = seg?.secondsFromStart;
  if (t == null) return "jump";
  const m = Math.floor(t / 60);
  const s = String(Math.round(t % 60)).padStart(2, "0");
  return `${m}:${s}`;
}

const groupedRows = computed(() => {
  const details = props.call?.outcomeDetails || {};
  const unanswered = new Set((verification.value.unansweredFields || []).map(snakeToCamel));
  const seen = new Set();
  const groups = [];
  for (const group of GROUPS) {
    const rows = [];
    for (const key of group.keys) {
      seen.add(key);
      const raw = details[key];
      const empty = raw == null || raw === "" || (Array.isArray(raw) && !raw.length);
      if (empty && !unanswered.has(key)) continue;
      const value =
        Array.isArray(raw) || (raw !== null && typeof raw === "object") ? JSON.stringify(raw) : String(raw ?? "");
      const segmentIndex = empty ? null : locateSegment(value);
      rows.push({
        key,
        label: fieldLabel(key),
        value,
        empty,
        segmentIndex,
        timestamp: segmentIndex !== null ? segmentTimestamp(segmentIndex) : "",
      });
    }
    if (rows.length) groups.push({ label: group.label, rows });
  }
  const extras = [];
  for (const key of unanswered) {
    if (seen.has(key)) continue;
    seen.add(key);
    extras.push({ key, label: fieldLabel(key), value: "", empty: true, segmentIndex: null, timestamp: "" });
  }
  for (const [key, raw] of Object.entries(details)) {
    if (META_KEYS.has(key) || seen.has(key)) continue;
    if (raw == null || raw === "" || (Array.isArray(raw) && !raw.length)) continue;
    const value = Array.isArray(raw) || typeof raw === "object" ? JSON.stringify(raw) : String(raw);
    const segmentIndex = locateSegment(value);
    extras.push({
      key,
      label: fieldLabel(key),
      value,
      empty: false,
      segmentIndex,
      timestamp: segmentIndex !== null ? segmentTimestamp(segmentIndex) : "",
    });
  }
  if (extras.length) groups.push({ label: "Other", rows: extras });
  return groups;
});

function exportRows() {
  const rows = [["Section", "Field", "Value"]];
  for (const group of groupedRows.value) {
    for (const row of group.rows) {
      rows.push([group.label, row.label, row.empty ? "" : row.value]);
    }
  }
  return rows;
}

function exportCsv() {
  const csv = exportRows()
    .map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `benefits-verification-${props.call?.orderInfo || props.call?.id}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
}

function exportPdf() {
  const w = window.open("", "_blank");
  if (!w) return;
  const esc = (x) =>
    String(x).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
  const rows = groupedRows.value
    .map(
      (g) =>
        `<tr class="g"><td colspan="2">${esc(g.label)}</td></tr>` +
        g.rows.map((r) => `<tr><td>${esc(r.label)}</td><td>${r.empty ? "—" : esc(r.value)}</td></tr>`).join("")
    )
    .join("");
  w.document.write(`<!doctype html><title>Benefits Verification</title><style>
    body{font:13px/1.5 -apple-system,'Segoe UI',sans-serif;color:#042143;padding:32px}
    h1{font-size:18px;margin:0 0 2px} p{color:#5E7898;margin:0 0 18px;font-size:12px}
    table{border-collapse:collapse;width:100%} td{border-bottom:1px solid #DCE1EA;padding:6px 10px;font-size:12.5px}
    .g td{font-weight:700;font-size:10.5px;text-transform:uppercase;letter-spacing:.08em;color:#5E7898;padding-top:16px;border-bottom:1px solid #9aa8bb}
  </style><h1>Benefits Verification</h1><p>Order ${
    props.call?.orderInfo || ""
  } · exported ${new Date().toLocaleString()}</p><table>${rows}</table>`);
  w.document.close();
  w.focus();
  w.print();
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.bv-report {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 10px 2px 12px;
}
.bv-report__band {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
}
.bv-report__left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.bv-report__score-eyebrow {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(94, 120, 152, 0.7);
  margin-bottom: 2px;
}
.bv-report__score {
  font-family: "JetBrains Mono", monospace;
  font-size: 64px;
  font-weight: 600;
  line-height: 0.9;
  letter-spacing: -0.04em;

  &--green {
    color: $lh_color_green;
  }
  &--amber {
    color: $lh_color_orange;
  }
  &--red {
    color: $lh_color_red;
  }
}
.bv-report__score-den {
  font-size: 20px;
  color: rgba(94, 120, 152, 0.45);
  font-weight: 500;
  letter-spacing: 0;
}
.bv-report__tier {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 2px 0 10px;

  &--green {
    color: $lh_color_green;
  }
  &--amber {
    color: $lh_color_orange;
  }
  &--red {
    color: $lh_color_red;
  }
}
.bv-report__ledger {
  font-size: 13px;
  max-width: 340px;
}
.bv-report__ledger-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 18px;
  padding: 3px 0;
}
.bv-report__ledger-reason {
  color: rgba(4, 33, 67, 0.72);
}
.bv-report__ledger-neg {
  color: $lh_color_red;
  font-family: "JetBrains Mono", monospace;
  font-size: 12.5px;
  flex-shrink: 0;
}
.bv-report__ledger-clean {
  color: $lh_color_green;
  font-size: 13px;
}
.bv-report__signals {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: rgba(94, 120, 152, 0.75);
  margin-top: 10px;
  letter-spacing: 0.01em;
}
.bv-report__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  flex-shrink: 0;
  text-align: right;
}
.bv-report__captured-num {
  font-family: "JetBrains Mono", monospace;
  font-size: 44px;
  font-weight: 600;
  line-height: 0.95;
  letter-spacing: -0.02em;
  color: $lh_color_navy_dark;
}
.bv-report__captured-den {
  font-size: 18px;
  color: rgba(94, 120, 152, 0.45);
  font-weight: 500;
}
.bv-report__captured-label {
  font-size: 10.5px;
  color: rgba(94, 120, 152, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 12px;
}
.bv-report__reverify {
  text-transform: none;
  letter-spacing: 0;
}
.bv-report__actions {
  display: flex;
  gap: 2px;
  margin-top: 6px;

  :deep(.v-btn) {
    text-transform: none;
    letter-spacing: 0;
    color: rgba(94, 120, 152, 0.85);
  }
}

.bv-report__table-wrap {
  overflow-x: auto;
}
.bv-report__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  th {
    text-align: left;
    white-space: nowrap;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(94, 120, 152, 0.65);
    padding: 0 12px 10px;
    border-bottom: 1px solid rgba(5, 46, 94, 0.1);
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(5, 46, 94, 0.05);
    vertical-align: baseline;
  }
  tr:last-child td {
    border-bottom: none;
  }
}
.bv-report__th-right {
  text-align: right !important;
}
.bv-report__group-row td {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: $lh_color_blue;
  padding: 26px 12px 8px;
  border-bottom: none;
}
.bv-report__cell-field {
  font-weight: 600;
  width: 34%;
  color: $lh_color_navy_dark;
}
.bv-report__cell-value {
  color: rgba(4, 33, 67, 0.8);
}
.bv-report__cell-ts {
  text-align: right;
  white-space: nowrap;
  width: 70px;
}
.bv-report__ts-chip {
  font-family: "JetBrains Mono", monospace;
  font-size: 11.5px;
  color: $lh_color_blue;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.85;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    opacity: 1;
  }
}
.bv-report__row--empty td {
  color: rgba(94, 120, 152, 0.7);
}
.bv-report__unanswered {
  font-size: 11px;
  color: rgba(94, 120, 152, 0.6);
  letter-spacing: 0.04em;
  font-style: italic;
}
</style>
